import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import HOMEPAGE from "@/config/homepage-content.json";

// Featured journeys are sourced from /app/frontend/src/config/homepage-content.json so
// the ops team can swap titles, categories, covers and links weekly without touching
// component code. The grid layout below maps the first 4 entries by index — keep at
// least 4 entries in the JSON to avoid empty cells.
const destinations = HOMEPAGE.featuredJourneys.map((d) => ({
  id: d.id,
  slug: d.slug,
  name: d.title,
  subtitle: d.subtitle,
  category: d.category,
  img: d.cover,
  link: d.link,
  size: d.size,
}));

export default function DestinationsGrid({ onPick }) {
  return (
    <section
      id="destinations"
      className="bg-white py-24 md:py-32 px-6 md:px-12"
      data-testid="destinations-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div className="max-w-xl">
            <span className="vp-label block mb-4">FEATURED JOURNEYS</span>
            <h2 className="font-serif-vp text-4xl md:text-5xl lg:text-6xl text-[var(--vp-primary)] leading-[1.05]">
              Where the quiet travellers<br />are going next.
            </h2>
          </div>
          <p className="text-[var(--vp-muted)] max-w-sm font-sans-vp">
            Each place chosen for its soul — not its algorithm. Click any to begin planning.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <DestCard d={destinations[0]} onPick={onPick} className="md:col-span-5 md:row-span-2 h-[560px]" />
          <DestCard d={destinations[1]} onPick={onPick} className="md:col-span-7 h-[270px]" />
          <DestCard d={destinations[2]} onPick={onPick} className="md:col-span-4 h-[270px]" />
          <DestCard d={destinations[3]} onPick={onPick} className="md:col-span-3 h-[270px]" />
        </div>
      </div>
    </section>
  );
}

function DestCard({ d, onPick, className = "" }) {
  return (
    <div className={`vp-card group relative w-full ${className}`} data-testid={`destination-${d.id}`}>
      <img
        src={d.img}
        alt={d.name}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
      <div className="relative z-10 h-full p-6 flex flex-col justify-between text-white">
        <div className="flex items-start justify-between">
          <span className="vp-label text-white/85">{d.category}</span>
          <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-serif-vp text-3xl md:text-4xl mb-3">{d.name}</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => onPick(d.name)}
              className="text-[11px] tracking-[0.18em] uppercase px-4 py-2 rounded-full bg-white text-[var(--vp-primary)] hover:bg-[var(--vp-accent)] transition-colors font-medium"
              data-testid={`destination-plan-${d.id}`}
            >
              Plan now
            </button>
            <Link
              to={`/destinations/${d.slug}`}
              className="text-[11px] tracking-[0.18em] uppercase px-4 py-2 rounded-full border border-white/50 text-white hover:bg-white hover:text-[var(--vp-primary)] transition-colors"
              data-testid={`destination-explore-${d.id}`}
            >
              Explore
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
